<template>
	<div
		v-show="!isBusy"
		:class="{ 'plaid-link': true, styled: !disableStyling }"
	>
		<section
			v-if="anyBanksToShow"
			class="accounts-connected"
		>
			<slot
				name="connectedBanks"
				:accountList="connectedAccounts"
				:updateAccount="(plaidItemId: string) => launchLink(plaidItemId)"
				:deleteAccount="(plaidItemId: string) => deleteItem(plaidItemId)"
			>
				<h2>Connected Banks</h2>

				<div class="connected-accounts-container">
					<div
						v-for="item in connectedAccounts"
						:key="item.plaidItemId"
						:index="item.plaidItemId"
						class="connected-bank-item"
					>
						<div class="connected-bank-item-header">
							<h1>{{ item.institution }}</h1>

							<TooltipComponent
								:tooltip="item.accountsDisplay"
								direction="top"
							>
								<template #showFromOutside>
									<span class="connected-bank-account-count">{{ item.accounts.length }}</span>
								</template>
							</TooltipComponent>
						</div>

						<div class="connected-bank-item-actions">
							<button
								:class="{
									'update-item-button': true,
									'login-needed': doesPlaidItemNeedUpdate(item),
								}"
								@click.stop="launchLink(item.plaidItemId)"
							>
								{{ item.status !== 0 ? 'Re-Login' : 'Update' }}
							</button>

							<button
								class="remove-item-button"
								@click.stop="deleteItem(item.plaidItemId)"
							>
								Remove
							</button>
						</div>
					</div>
				</div>
			</slot>
		</section>

		<!-- When enabled, Plaid will inject content into any element with the ID "plaid-embedded-link-container" -->
		<section
			v-if="useEmbeddedInstitutionSearch"
			:id="EMBEDDED_LINK_CONTAINER_ID"
			class="plaid-embedded"
		/>

		<section
			v-else
			class="plaid-launch"
		>
			<header
				v-if="showFailureScreen && !disableFailureScreen"
				class="failure-screen"
			>
				<slot name="failureScreen"></slot>
			</header>
			<header
				v-else-if="needToLaunchLink"
				class="initial-screen"
			>
				<slot name="prelaunchScreen"></slot>
			</header>
			<header
				v-else
				class="success-screen"
			>
				<slot name="successScreen"></slot>
			</header>

			<slot
				v-if="showLaunchLinkButton"
				name="launchLinkButton"
				:action="() => launchLink()"
				:buttonText="launchLinkText"
			>
				<button
					class="launch-link-button"
					@click="launchLink()"
				>
					{{ launchLinkText }}
				</button>
			</slot>
			<slot
				v-if="showRejectLinkButton"
				name="rejectLinkButton"
				:action="() => $emit('rejectLink')"
				:buttonText="rejectText"
			>
				<button
					class="reject-link-button"
					@click="$emit('rejectLink')"
				>
					{{ rejectText }}
				</button>
			</slot>
		</section>
	</div>
</template>
<script lang="ts">
	// ===================
	// INTERNAL INTERFACES
	// ===================
	// #region Internal Interfaces
	export interface IPlaidConnectedAccount {
		/** The Item ID of the connected bank login */
		plaidItemId: string;

		/** The name of the bank institution */
		institution: string;

		/** A list of accounts and associated metadata of the connected bank login */
		accounts: PlaidBankAccount[];

		/** The name of each account followed by its mask*/
		accountsDisplay: string[];

		/** A list of products currently active for this Plaid Item */
		activeProducts: string[];

		/** Uses ENUMS.TOKEN_STATUS. The current state of the bank login's access token. */
		status: number;
	}

	export interface IPlaidItemInfo {
		institution?: {
			name?: string;
			id?: string;
		};
		accounts?: {
			/** The Plaid `account_id` */
			id?: string;

			/** The official account name */
			name?: string;

			/** The last 2-4 alphanumeric characters of an account's official account number. */
			mask?: string;

			/** The account type. See Plaid Docs for available values: https://plaid.com/docs/api/accounts/#account-type-schema */
			type?: string;

			/** The account subtype. See Plaid Docs for available values: https://plaid.com/docs/api/accounts/#account-type-schema */
			subtype?: string;
		}[];
	}

	export interface IPlaidLinkProps {
		/** REQUIRED, contains the data needed to initialize a Link Token */
		linkConfig: PlaidLinkConfig;

		/** Needed for report-generating purposes when this component makes any Plaid API call. */
		storeCode?: string;

		/** Link Token created outside of this component; will skip all init tasks and immediately start a session */
		externalLinkToken?: string;

		/** Required if passing in an external Link Token that's set to update mode. The Item ID of the Item getting updated. */
		externalUpdateItemId?: string;

		/** When accessing after redirect from OAuth institution (window.location.href); when provided will immediately relaunch Link from where user left off */
		receivedRedirectUrl?: string;

		/** Text inside the "Launch Plaid" button */
		launchText?: string;

		/** Text inside the "Reject Plaid" button */
		rejectText?: string;

		/** When true, will launch as soon as everything has been loaded. */
		launchOnLoad?: boolean;

		/** Refer to <https://plaid.com/docs/link/embedded-institution-search/> for details */
		useEmbeddedInstitutionSearch?: boolean;

		/** When true, the success screen will always show at the end of a Link session, regardless of whether or not the user passed the session. */
		disableFailureScreen?: boolean;

		/** If true, will hide the list of connected banks that appears when init-ing a banking product */
		hideConnectedBanks?: boolean;

		/** When a Link session successfully completes, hide the "Start Link" session button on the success screen.*/
		preventRelaunchAfterLinkSuccess?: boolean;

		/** If true, just prints out a lot more console log lines. LinkSession responses also won't be saved to Elastic */
		debugMode?: boolean;

		/** If you want to override all internal styling */
		disableStyling?: boolean;

		/** Language passed for dynamic text */
		customerLanguage?: string;
	}
	// #endregion
</script>
<script setup lang="ts">
	// Landing screen to initialize Plaid Link (refer to: https://plaid.com/docs/link/)
	// Sandbox Test Credentials: https://plaid.com/docs/sandbox/test-credentials/
	// NOTE: Requires some kind of customer ID => at least one of the Associations MUST be Customer/CustomerApp association

	import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';

	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import util from '@core/services/util';

	import { ConvertPlaidErrorToCamelCase, PlaidError } from '@core/classes/Plaid/PlaidError';
	import { DOC_ASSOCIATION, PLAID_PRODUCTS, PLAID_VERIFICATION_STATUS } from '@core/classes/Enums';
	import { PlaidLinkEventMetadata, PlaidLinkExitMetadata, PlaidLinkSuccessMetadata } from '@core/classes/Plaid/PlaidLinkMetadata';
	import { CamelKeysToSnakeDeep } from '@core/classes/UtilClasses';
	import { PlaidBankAccount } from '@core/classes/Plaid/PlaidBankAccount';
	import { PlaidCustomerInfo } from '@core/classes/Plaid/PlaidCustomerInfo';
	import PlaidLinkConfig from '@core/classes/Plaid/PlaidLinkConfig';

	import modalInfo from '@core/modals/modalInfo.vue';
	import TooltipComponent from '@core/components/TooltipComponent.vue';

	const props = defineProps<IPlaidLinkProps>();

	const emit = defineEmits<{
		isBusyUpdate: [currentState: boolean];

		plaidCustomerUpdate: [currentValue: PlaidCustomerInfo];

		/** When Link reaches end of flow OR when a bank account gets successfully connected.
		 *  Plaid Docs: https://plaid.com/docs/link/web/#onsuccess
		 */
		success: [public_token: string | undefined, metadata: PlaidLinkSuccessMetadata];

		/** When a user exits Link without successfully linking an item OR when an error occurs during the Link session
		 *  Plaid Docs: https://plaid.com/docs/link/web/#onexit
		 */
		exit: [error: PlaidError | undefined, metadata: PlaidLinkExitMetadata];

		/** Triggered when various events occur during the Link session
		 *  Plaid Docs: https://plaid.com/docs/link/web/#onevent
		 */
		event: [eventName: string, metadata: PlaidLinkEventMetadata];

		/** When this component encounters any error while pre-initing or post-initing */
		error: [errType: string, errMessage: string, isWarning: boolean];

		/** When the user fails the Plaid Link session (currently only fires after an IDV session)
		 *  Note: may fire alongside a @success event. @success fires at END-OF-FLOW, not if the flow ends in a "good" state
		 */
		failure: [currState: boolean];

		/** When the "Reject" button is clicked (if available) */
		rejectLink: [];

		/** Emits the results of the IDV session (if applicable)
		 *  Plaid Docs: https://plaid.com/docs/api/products/identity-verification/#identity_verificationget
		 */
		identityVerified: [idvObj: any];

		/** Triggered when all post-Link activities have been successfully completed. Will not emit if any errors occur during post-Link activities.
		 *  Plaid Docs: https://plaid.com/docs/link/web/#onsuccess (for metadata schema)
		 */
		postLinkComplete: [metadata: PlaidLinkSuccessMetadata];
	}>();

	// ==================
	// REACTIVE VARIABLES
	// ==================
	// #region Reactive Variables
	const EMBEDDED_LINK_CONTAINER_ID = 'plaid-embedded-link-container';

	const plaidCustomerInfo = ref<PlaidCustomerInfo>(new PlaidCustomerInfo());
	watch(plaidCustomerInfo, newVal => {
		emit('plaidCustomerUpdate', newVal);
	});

	const isBusy = ref(false);
	watch(isBusy, newVal => {
		emit('isBusyUpdate', newVal);
	});

	const showFailureScreen = ref(false);
	watch(showFailureScreen, newVal => {
		emit('failure', newVal);
	});

	const isExternalScriptLoaded = ref(false);
	const isLinkSuccessful = ref(false);
	const isIdvSuccessful = ref(false);

	const linkToken = ref<string>(null);
	const linkTokenForUpdate = ref<string>(null);
	const plaidItemIdForUpdate = ref<string>(null);

	const linkHandler = ref(null);

	const anyBanksToShow = computed((): boolean => {
		return !props.hideConnectedBanks && !hasIdentityVerification.value && !isPayrollIncome.value && connectedAccounts.value.length > 0;
	});

	const needToLaunchLink = computed((): boolean => {
		if (hasIdentityVerification.value && !props.disableFailureScreen) return !isIdvSuccessful.value;

		return !isLinkSuccessful.value;
	});

	const launchLinkText = computed((): string => {
		if (props.launchText) return props.launchText;
		if (hasIdentityVerification.value) {
			const text = props.customerLanguage == 'Spanish' ? 'Verifica tu identidad' : 'Verify Identity';
			return text;
		}
		if (hasTransfer.value) return 'Transfer Funds';
		return plaidCustomerInfo.value.accessTokens.length > 0 ? 'Connect Another Account' : 'Connect Account';
	});

	const hasIdentityVerification = computed((): boolean => {
		return props.linkConfig.hasIdentityVerification();
	});

	const isPayrollIncome = computed((): boolean => {
		return props.linkConfig.isPayrollIncome();
	});

	const hasTransfer = computed((): boolean => {
		return props.linkConfig.hasTransfer();
	});

	const connectedAccounts = computed((): IPlaidConnectedAccount[] => {
		if (!plaidCustomerInfo.value?.accessTokens) return [];

		const accountList = [];
		for (const token of plaidCustomerInfo.value.accessTokens) {
			// If using Transfer, can only use tokens that have Transfer active on them
			if (hasTransfer.value && token.activeProducts.some(p => p === PLAID_PRODUCTS.Transfer)) continue;

			accountList.push({
				plaidItemId: token.plaidItemId,
				institution: token.institution.name,
				accounts: token.accounts,
				accountsDisplay: token.accounts.map((a: any) => `${a.name} (...${a.mask})`),
				activeProducts: token.activeProducts,
				status: token.status,
			});
		}

		return accountList;
	});

	const showLaunchLinkButton = computed((): boolean => {
		return (
			(needToLaunchLink.value && !showFailureScreen.value) ||
			(!needToLaunchLink.value && !hasIdentityVerification.value && !isPayrollIncome.value && !props.preventRelaunchAfterLinkSuccess)
		);
	});

	const showRejectLinkButton = computed((): boolean => {
		return props.rejectText && !showFailureScreen.value && needToLaunchLink.value;
	});
	// #endregion

	// =========================
	// COMPONENT LIFECYCLE HOOKS
	// =========================
	// #region Lifecycle Hooks
	onBeforeMount(async () => {
		isBusy.value = true;

		try {
			loadExternalPlaidScripts();

			if (!(await initPlaidCustomerInfo())) return;

			// SKIP PRE-INIT IF LINK TOKEN IS PASSED IN AS PROP
			if (props.externalLinkToken) {
				createCallbackOnScriptLoad(() => {
					tryLaunchFromExternalToken();
				});
				return;
			}

			// OAUTH REDIRECT - TRY TO RELAUNCH WHERE USER LEFT OFF
			if (props.receivedRedirectUrl) {
				createCallbackOnScriptLoad(() => {
					linkToken.value = getStoredLinkToken();
					launchLink();
				});

				return;
			} else {
				localStorage.removeItem('link_token');
			}

			await preInitPlaidProducts();

			if (props.useEmbeddedInstitutionSearch)
				createCallbackOnScriptLoad(() => {
					createEmbeddedLink();
				});
			else if (props.launchOnLoad)
				createCallbackOnScriptLoad(() => {
					launchLink();
				});
		} catch (err) {
			errorFromServer('UNKNOWN_ERROR', err.toString());
		} finally {
			isBusy.value = false;
		}
	});

	onBeforeUnmount(() => {
		linkHandler.value?.destroy();
	});
	// #endregion

	// =====================
	// PRE-LINK INIT METHODS
	// =====================
	// #region Pre-Link Init Methods
	const loadExternalPlaidScripts = () => {
		// Attaches external Plaid scripts to head if they don't exist yet
		const scriptLink = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

		if (!document.head.querySelector(`[src="${scriptLink}"]`)) {
			const scriptElem = document.createElement('script');
			scriptElem.setAttribute('src', scriptLink);
			scriptElem.setAttribute('type', 'text/javascript');
			scriptElem.onload = () => {
				isExternalScriptLoaded.value = true;
			};
			scriptElem.onerror = () => {
				errorFromServer('SCRIPT_LOAD', 'Could not load script. Try refreshing the page to try again.');
			};
			document.head.appendChild(scriptElem);
		} else {
			isExternalScriptLoaded.value = true;
		}
	};

	const createCallbackOnScriptLoad = (callbackFunction: () => any) => {
		if (isExternalScriptLoaded.value) {
			callbackFunction();
			return;
		}

		watch(
			isExternalScriptLoaded,
			newVal => {
				if (!newVal) return;
				callbackFunction();
			},
			{ once: true },
		);
	};

	/** Tries to fetch a link token stored in local storage. If it doesn't exist or is expired, returns null. */
	const getStoredLinkToken = (): string => {
		const tokenItem = JSON.parse(localStorage.getItem('link_token'));
		const tokenValid = tokenItem ? util.isFutureDate(new Date(tokenItem.expiration)) : false;

		return tokenValid ? tokenItem.token : null;
	};

	/** Tries to fetch a PlaidCustomerInfo record by using a customer association found in the LinkConfig. Returns false if any errors occur. */
	const initPlaidCustomerInfo = async (): Promise<boolean> => {
		// TODO: I feel like there's a better way to get the customer association besides searching the associations list.
		const customerAssociation = props.linkConfig.associations.find(a => {
			return a.type === DOC_ASSOCIATION.CustomerApplication || a.type === DOC_ASSOCIATION.Customer;
		});

		if (!customerAssociation) {
			errorFromServer('INVALID_TYPE', 'Association array is missing a customer/customer application association. Cannot proceed with initialization tasks.');
			return false;
		}

		plaidCustomerInfo.value.customerId = customerAssociation.id;
		plaidCustomerInfo.value.customerIdType = customerAssociation.type;

		await getExistingCustomer();
		return true;
	};

	/** Fetches an existing PlaidCustomerInfo record from ElasticSearch.
	 * Errors from this function are OK, so they won't get forwarded to parent unless debug mode is enabled
	 */
	const getExistingCustomer = async (): Promise<void> => {
		const response = await api.plaid.findExistingCustomer(plaidCustomerInfo.value.customerId);
		if (!response?.data) return;

		if (response.data.error) {
			if (props.debugMode) errorFromServer("RECORD DOESN'T EXIST", 'Could not find an existing customer Plaid record for the given ID.', true);
			return;
		}

		plaidCustomerInfo.value = response.data.plaidCustomerInfo;
	};

	/** Creates a user token that's needed for all Income products */
	const createUserToken = async (customerId: string, customerIdType: number) => {
		isBusy.value = true;

		try {
			const userTokenResponse = await api.plaid.createIncomeUser(customerId, customerIdType, props.storeCode);
			if (checkForResponseError(userTokenResponse?.data, 'CREATE USER TOKEN')) return;

			if (!plaidCustomerInfo.value.id)
				// New customer was created in last API call. Fetch latest info.
				await getExistingCustomer();
			else plaidCustomerInfo.value.userToken = userTokenResponse.data;
		} catch (err) {
			errorFromServer('UNKNOWN_ERROR', err.toString());
		} finally {
			isBusy.value = false;
		}
	};

	/** Makes a request for an incoming transfer, which is needed before launching Transfer UI */
	const getTransferIntentId = async (): Promise<void> => {
		const transferResponse = await props.linkConfig.getTransferIntentId();
		checkForResponseError(transferResponse?.data, 'GET TRANSFER INTENT ID');
	};

	/** If enough information is available, tries to prepopulate an IDV session with PII.
	 * If they end up failing Lightning Verification, requests for a session retry (requiring manually PII entry)
	 */
	const prepopulateIdentityVerification = async (): Promise<void> => {
		if (!props.linkConfig.userData || props.linkConfig.userData.isPracticallyEmpty()) return;

		isBusy.value = true;
		try {
			const prepopulateResponse = await props.linkConfig.prepopulateIdentityVerification(plaidCustomerInfo.value.customerId, props.storeCode);
			if (prepopulateResponse !== undefined && checkForResponseError(prepopulateResponse?.data, 'PREPOPULATE IDENTITY INFORMATION')) return;

			const status = prepopulateResponse.data.status;
			if (status === PLAID_VERIFICATION_STATUS.Success.str) {
				isIdvSuccessful.value = true;
			} else if (status === PLAID_VERIFICATION_STATUS.Failed.str || status === PLAID_VERIFICATION_STATUS.Canceled.str) {
				const retryResponse = await api.plaid.retryIdentityVerification(
					props.linkConfig.templateId,
					plaidCustomerInfo.value.customerId,
					props.linkConfig.userData,
					props.storeCode,
					props.linkConfig.associations,
				);

				if (checkForResponseError(retryResponse?.data, 'RETRY IDENTITY VERIFICATION')) return;

				props.linkConfig.idvSessionId = retryResponse.data.id;
				props.linkConfig.templateId = retryResponse.data.template.id;
			}
		} catch (err) {
			errorFromServer('UNKNOWN_ERROR', err.toString());
		} finally {
			isBusy.value = false;
		}
	};

	/** Checks products requested in LinkConfig and preforms any necessary pre-initialization tasks */
	const preInitPlaidProducts = async (): Promise<void> => {
		if (props.linkConfig.canPrepopulateIDV()) await prepopulateIdentityVerification();

		const hasIncomeVerification = props.linkConfig.hasIncomeVerification();
		if (hasIncomeVerification && !plaidCustomerInfo.value.userToken)
			await createUserToken(plaidCustomerInfo.value.customerId, plaidCustomerInfo.value.customerIdType);

		if (hasTransfer.value && props.linkConfig.transferIntent) await getTransferIntentId();
	};

	/** Creating the Link token needed to initialize a Link session */
	const createLinkToken = async (plaidItemId?: string): Promise<void> => {
		if (!plaidItemId && linkToken.value) return;

		const createTokenResponse = await props.linkConfig.createLinkToken(plaidCustomerInfo.value, plaidItemId);
		if (checkForResponseError(createTokenResponse?.data, 'CREATE LINK TOKEN')) return;

		if (plaidItemId) {
			linkTokenForUpdate.value = createTokenResponse.data.linkToken;
			plaidItemIdForUpdate.value = plaidItemId;
		} else {
			const linkInfo = {
				token: createTokenResponse.data.linkToken,
				expiration: createTokenResponse.data.expiration,
			};
			localStorage.setItem('link_token', JSON.stringify(linkInfo));

			linkToken.value = linkInfo.token;
		}
	};
	// #endregion

	// ===================
	// LINK LAUNCH METHODS
	// ===================
	// #region Link Launch Methods
	/** Given an external link token, if able to be verified, immediately launches a Link session. Returns whether the Link session was able to be launched. */
	const tryLaunchFromExternalToken = async (): Promise<boolean> => {
		if (!props.externalLinkToken) return false;

		const linkTokenResponse = await api.plaid.getLinkToken(props.externalLinkToken);
		if (checkForResponseError(linkTokenResponse, 'CHECK EXTERNAL LINK TOKEN')) return false;

		const tokenInfo = linkTokenResponse.data;

		if (!util.isFutureDate(new Date(tokenInfo.expiration))) {
			errorFromServer('INVALID_LINK_TOKEN', 'The external link token is expired. Generate a new link token to proceed.');
			return false;
		}

		props.linkConfig.populateWithLinkTokenMetadata(tokenInfo.metadata);
		linkToken.value = props.externalLinkToken;
		await launchLink();

		return true;
	};

	/** Generates a Link Token (if needed), and then launches the Plaid Link session. If a Plaid Item ID is provided, will launch Link in update mode. */
	const launchLink = async (plaidItemId?: string): Promise<void> => {
		isBusy.value = true;

		try {
			await createLinkToken(plaidItemId);

			if ((plaidItemId && !linkTokenForUpdate.value) || (!plaidItemId && !linkToken.value)) {
				errorFromServer('MISSING_LINK_TOKEN', 'Unable to generate a Link Token. Cannot proceed with initializing Link.');
				return;
			}

			linkHandler.value = (window as any).Plaid.create({
				token: plaidItemId ? linkTokenForUpdate.value : linkToken.value,
				onSuccess: linkOnSuccess,
				onLoad: linkOnLoad,
				onExit: linkOnExit,
				onEvent: linkOnEvent,
				receivedRedirectUri: props.receivedRedirectUrl,
			});

			linkHandler.value.open();
		} catch (err) {
			errorFromServer('UNKNOWN_ERROR', err.toString());
		} finally {
			isBusy.value = false;
		}
	};

	/** Generates a Link Token (if needed), and then injects the embedded institution search widget into the DOM. */
	const createEmbeddedLink = async (): Promise<void> => {
		if (!props.useEmbeddedInstitutionSearch) return;

		const embedContainer = document.getElementById(EMBEDDED_LINK_CONTAINER_ID);
		if (!embedContainer) {
			errorFromServer('MISSING_CONTAINER', 'The embedded link container has not been mounted yet.');
			return;
		}

		isBusy.value = true;
		try {
			await createLinkToken();

			if (!linkToken.value) {
				errorFromServer('MISSING_LINK_TOKEN', 'Unable to generate a Link Token. Cannot proceed with initializing Link.');
				return;
			}

			(window as any).Plaid.createEmbedded(
				{
					token: linkToken.value,
					onSuccess: linkOnSuccess,
					onLoad: linkOnLoad,
					onExit: linkOnExit,
					onEvent: linkOnEvent,
				},
				embedContainer,
			);
		} catch (err) {
			errorFromServer('UNKNOWN_ERROR', err.toString());
		} finally {
			isBusy.value = false;
		}
	};
	// #endregion

	// ====================
	// LINK EVENT CALLBACKS
	// ====================
	// #region Link Event Callbacks
	const linkOnSuccess = async (public_token: string, metadata: PlaidLinkSuccessMetadata): Promise<void> => {
		// Called by Link iframe when session COMPLETES successfully (verifying identity, linking an item) / reaches end of flow
		// If not in debug mode, uploads the session reference to ElasticSearch.
		// Exchanges public_token for Access Token (if applicable), may fetch additional session information depending on product.
		isBusy.value = true;

		try {
			isLinkSuccessful.value = true;
			localStorage.removeItem('link_token');

			emit('success', public_token, metadata);

			if (props.debugMode) {
				/* eslint-disable no-console */
				console.group('%cLink Has Exited Successfully', 'font-weight: bold; color: green;');
				console.log('Public Token: ', public_token);
				console.log('Metadata: ', metadata);
				console.groupEnd();
				/* eslint-enable */
			}

			if (!props.debugMode && props.linkConfig.associations) {
				const linkSession = { linkSessionId: metadata.link_session_id };
				await api.plaid.storeLinkSession(true, linkSession, props.storeCode, props.linkConfig.associations);
			}

			// Data not available until webhook event gets triggered
			if (isPayrollIncome.value || props.linkConfig.enableMultiItemLink || !public_token) return;

			// For IDV sessions, grab the session data from Plaid to check for status.
			if (hasIdentityVerification.value) {
				const response = await api.plaid.getIdentityVerification(metadata.link_session_id);
				if (checkForResponseError(response?.data, 'GET IDENTITY VERIFICATION DATA')) return;

				emit('identityVerified', response.data);
				// eslint-disable-next-line no-console
				if (props.debugMode) console.log('Identity Verification: ', response.data);

				const responseStatus = response.data.plaidResponse.status;
				isIdvSuccessful.value = responseStatus === PLAID_VERIFICATION_STATUS.Success.str;
				showFailureScreen.value = !isIdvSuccessful.value;
				return;
			}

			// Otherwise, is a banking product - Generate an access token
			if (linkTokenForUpdate.value || props.externalUpdateItemId) {
				// Link was completed in update mode — already have access token
				const response = await api.plaid.updateAccessToken(plaidCustomerInfo.value.customerId, plaidItemIdForUpdate.value ?? props.externalUpdateItemId);
				if (!checkForResponseError(response?.data, 'UPDATE ACCESS TOKEN IN ELASTIC')) {
					plaidCustomerInfo.value = response.data;
					linkTokenForUpdate.value = null;
					plaidItemIdForUpdate.value = null;
				}
			} else {
				const itemInfo = {
					institution: {
						id: metadata.institution.institution_id,
						name: metadata.institution.name,
					},
					accounts: metadata.accounts,
				};

				await exchangePublicToken(public_token, itemInfo);
			}
		} catch (err) {
			errorFromServer('UNKNOWN_ERROR', err.toString());
		} finally {
			isBusy.value = false;
			linkHandler.value?.destroy();
			emit('postLinkComplete', metadata);
		}
	};

	/** Called by Link iframe when the Link module has finished loading */
	const linkOnLoad = (): void => {
		// eslint-disable-next-line no-console
		if (props.debugMode) console.log('%cLink Load Event.', 'font-weight: bold; color: orange;');
	};

	/** Called by Link iframe when a user exits Link without successfully linking an Item, or when an error occurs during Link initialization.
	 * If not in debug mode, will try to upload error data to ElasticSearch
	 */
	const linkOnExit = async (err: CamelKeysToSnakeDeep<PlaidError>, metadata: PlaidLinkExitMetadata): Promise<void> => {
		emit('exit', ConvertPlaidErrorToCamelCase(err), metadata);

		if (err && err.error_code === 'INVALID_LINK_TOKEN') {
			linkToken.value = null;
			localStorage.removeItem('link_token');
		}

		if (linkTokenForUpdate.value) {
			linkTokenForUpdate.value = null;
			plaidItemIdForUpdate.value = null;
		}

		if (props.debugMode) {
			/* eslint-disable no-console */
			console.group('%cLink exited.', 'font-weight: bold; color: red;');
			console.log('Error: ', err);
			console.log('Metadata: ', metadata);
			console.groupEnd();
			/* eslint-enable */
		}

		if (!props.debugMode && props.linkConfig.associations) {
			const linkSession: any = {
				linkSessionId: metadata.link_session_id,
				requestId: metadata.request_id,
			};
			if (err) {
				linkSession.error = {
					errorType: err.error_type,
					errorCode: err.error_code,
					errorMessage: err.error_message,
					displayMessage: err.display_message,
				};
			}
			await api.plaid.storeLinkSession(false, linkSession, props.storeCode, props.linkConfig.associations);
		}

		linkHandler.value?.destroy();
	};

	/** Called by Link iframe during various events that can happen during a Link session.
	 * Not guaranteed to fire exactly at the time of a user action in a Link session
	 */
	const linkOnEvent = (eventName: string, metadata: PlaidLinkEventMetadata) => {
		emit('event', eventName, metadata);

		if (props.debugMode) {
			/* eslint-disable no-console */
			console.group(`%c EVENT: ${eventName}`, 'font-weight: bold;');
			console.log('MetaData: ', metadata);
			console.groupEnd();
			/* eslint-enable */
		}
	};
	// #endregion

	// =================
	// POST-LINK METHODS
	// =================
	// #region Post-Link Methods
	/** Exchanging a public token for an access token (and creating a billable Plaid Item) */
	const exchangePublicToken = async (publicToken: string, itemInfo: IPlaidItemInfo) => {
		// Access Tokens from Payroll are completely useless as of right now.
		if (isPayrollIncome.value) return;

		isBusy.value = true;
		try {
			const itemMatch = plaidCustomerInfo.value.accessTokens?.some(item => doItemsMatch(itemInfo, item));
			if (itemMatch) {
				errorFromServer('DUPLICATE_ITEM', 'Duplicate Item Detected. Halting exchange for access token.', true);
				return;
			}

			const customerId = plaidCustomerInfo.value.customerId;
			const customerIdType = plaidCustomerInfo.value.customerIdType;

			const response = await api.plaid.accessTokenExchange(publicToken, customerId, customerIdType, props.storeCode);

			if (checkForResponseError(response?.data, 'ACCESS TOKEN EXCHANGE')) return;
			plaidCustomerInfo.value = response.data;
		} catch (err) {
			errorFromServer('UNKNOWN_ERROR', err.toString());
		} finally {
			isBusy.value = false;
		}
	};
	// #endregion

	// =======================
	// ITEM MANAGEMENT METHODS
	// =======================
	// #region Item Management Methods
	/** Revokes an access token (making it unusable for any future API calls) */
	const deleteItem = (plaidItemId: string): void => {
		$modal.open(modalInfo, {
			name: 'modalInfo',
			passedData: {
				title: 'Remove Account',
				info:
					'Are you sure you want to remove access to this account? ' +
					'We will not be able to access your account details until you log back into this account.',
				acceptText: 'Remove',
				acceptTextClass: 'button button-unwind',
				cancelText: 'Cancel',
				cancelTextClass: 'button',
			},
			postFunction: async () => {
				isBusy.value = true;
				try {
					const response = await api.plaid.deleteItem(plaidCustomerInfo.value.customerId, plaidItemId);
					if (checkForResponseError(response?.data, 'DELETE ITEM')) return;

					plaidCustomerInfo.value = response.data;
				} catch (err) {
					errorFromServer('UNKNOWN_ERROR', err.toString());
				} finally {
					isBusy.value = false;
				}
			},
		});
	};
	// #endregion

	// ====================
	// OTHER HELPER METHODS
	// ====================
	// #region Other Helper Methods
	/** Checking if two access tokens represent the same or overlapping information */
	const doItemsMatch = (item1: IPlaidItemInfo, item2: IPlaidItemInfo): boolean => {
		if (item1.institution.id !== item2.institution.id) return false;

		return item1.accounts.some((a1: any) => {
			return item2.accounts.some((a2: any) => {
				return a1.mask === a2.mask && a1.name === a2.name;
			});
		});
	};

	/** For a given Plaid Item, checks if the user needs to re-authenticate their account */
	const doesPlaidItemNeedUpdate = (plaidItem: IPlaidConnectedAccount): boolean => {
		// If the Item status is anything but active (expired, nearly expired, etc.)
		if (plaidItem.status !== 0) return true;

		// Checking if any products are missing from the Plaid Item
		const productsList = props.linkConfig.productsToInit;
		return productsList.some(p => plaidItem.activeProducts.every((ap: any) => ap !== p));
	};

	/** Checks a Plaid API response for any errors and then properly emits the error (if applicable) */
	const checkForResponseError = (responsePayload: any, step: string): boolean => {
		//
		if (!responsePayload) {
			errorFromServer('NO_RESPONSE', `No response from server during ${step} step.`);
			return true;
		}

		const plaidError = responsePayload.error;
		if (!plaidError) return false;

		errorFromServer(plaidError.errorCode, plaidError.errorMessage);
		return true;
	};

	/** Emits the given error. If in debug mode, also prints the error/warning to the console. */
	const errorFromServer = (type: string, message: string, isWarning = false) => {
		emit('error', type, message, isWarning);

		if (props.debugMode && isWarning) console.warn(`${type}: ${message}`);
		else if (props.debugMode) console.error(`${type}: ${message}`);
	};
	// #endregion
</script>
<style>
	.plaid-link.styled button {
		padding: 10px 5px;
		max-width: 200px;
		display: block;
	}

	.plaid-link.styled .plaid-embedded {
		height: 350px;
	}

	.plaid-link.styled .plaid-launch button {
		margin: 10px;
	}

	.plaid-link.styled .plaid-launch .reject-link-button {
		background-color: transparent;
		color: var(--text-color);
	}

	.plaid-link.styled .connected-accounts-container {
		display: flex;
		gap: 10px;
		flex-wrap: wrap;
		margin: 10px 0;
	}

	.plaid-link.styled .connected-bank-item {
		border: 1px solid var(--border-color);
		border-radius: var(--default-border-radius);
	}

	.plaid-link.styled .connected-bank-item:has(.update-item-button.login-needed) {
		border-color: var(--print-color);
	}

	.plaid-link.styled .connected-bank-item .connected-bank-item-header {
		padding: 10px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		border-radius: inherit;
		border-bottom: 1px solid var(--border-color);
		background-color: var(--card-background-color);
	}

	.plaid-link.styled .connected-bank-item h1 {
		margin: 0;
	}

	.plaid-link.styled .connected-bank-item:has(.update-item-button.login-needed) h1 {
		color: var(--print-color);
	}

	.plaid-link.styled .connected-bank-item .connected-bank-account-count {
		width: 15px;
		height: 15px;
		padding: 5px;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 2px solid var(--main-color);
		border-radius: 100%;
		color: var(--main-color);
		font-weight: bold;
	}

	.plaid-link.styled .connected-bank-item .connected-bank-item-actions {
		padding: 10px;
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 10px;
	}

	.plaid-link.styled .connected-bank-item .connected-bank-item-actions .remove-item-button {
		background-color: var(--error-color);
		border-color: transparent;
	}

	.plaid-link.styled .connected-bank-item .connected-bank-item-actions .update-item-button {
		background-color: var(--accept-color);
		border-color: transparent;
	}

	.plaid-link.styled .connected-bank-item .connected-bank-item-actions .update-item-button.login-needed {
		background-color: var(--print-color);
		animation: shadowPulse 1.5s infinite;
	}

	.plaid-link.styled .connected-bank-item .connected-bank-item-actions .update-item-button.login-needed:hover {
		animation: unset;
	}

	@keyframes shadowPulse {
		0% {
			transform: scale(0.98);
			box-shadow: 0 0 0 0 rgba(247, 150, 70, 0.7);
		}

		70% {
			transform: scale(1);
			box-shadow: 0 0 0 10px rgba(247, 150, 70, 0);
		}

		100% {
			transform: scale(0.98);
			box-shadow: 0 0 0 0 rgba(247, 150, 70, 0);
		}
	}

	.customer-portal-button-wrapper {
		margin: 0 auto;
		width: 50%;
	}
</style>
